/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import {
  CommissionsScreen as CommissionsPanelScreen,
  MyCommissionsScreen,
  CommissionPlansScreen,
  BoothRentalsScreen,
} from '../../../commissions/public-api';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { HubSegmentChips, type HubSegmentChip } from './HubSegmentChips';

type CommissionsSegment = 'panel' | 'mine' | 'plans' | 'rentals';

/** Route params accepted by CommissionsDashboard for deep-linking to a segment. */
export type CommissionsHubRouteParams = {
  initialSegment?: CommissionsSegment;
};

const SEGMENTS: CommissionsSegment[] = ['panel', 'mine', 'plans', 'rentals'];

const SEGMENT_CHIPS: Array<HubSegmentChip<CommissionsSegment>> = [
  { key: 'panel', label: 'Panel', testID: 'commissions-hub.segment.panel' },
  { key: 'mine', label: 'Mis comisiones', testID: 'commissions-hub.segment.mine' },
  { key: 'plans', label: 'Planes', testID: 'commissions-hub.segment.plans' },
  { key: 'rentals', label: 'Alquileres', testID: 'commissions-hub.segment.rentals' },
];

/**
 * Hub de Comisiones: una sola puerta en el nav (Panel | Mis comisiones | Planes | Alquileres).
 * Monta las pantallas existentes sin tocarlas — cada una conserva su propio header
 * con filtros, presets de fecha y acciones (por eso el hub no agrega header propio).
 */
export function CommissionsHubScreen() {
  const route = useRoute<any>();
  const initialSegmentParam = route.params?.initialSegment as CommissionsSegment | undefined;
  const [segment, setSegment] = useState<CommissionsSegment>(
    initialSegmentParam && SEGMENTS.includes(initialSegmentParam)
      ? initialSegmentParam
      : 'panel',
  );

  // Re-apply initialSegment when the route param changes (deep-link to the same
  // screen with a different segment).
  useEffect(() => {
    const next = route.params?.initialSegment as CommissionsSegment | undefined;
    if (next && SEGMENTS.includes(next)) {
      setSegment(next);
    }
  }, [route.params?.initialSegment]);

  return (
    <View style={styles.root}>
      <HubSegmentChips
        segments={SEGMENT_CHIPS}
        active={segment}
        onChange={setSegment}
      />
      <View style={styles.body}>
        {segment === 'panel' ? <CommissionsPanelScreen /> : null}
        {segment === 'mine' ? <MyCommissionsScreen /> : null}
        {segment === 'plans' ? <CommissionPlansScreen /> : null}
        {segment === 'rentals' ? <BoothRentalsScreen /> : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  body: { flex: 1, minHeight: 0 },
});
